import type { BalanceData, AprData, PostTradeMetrics } from '../utils'
import type { LenderYields } from '../../../../types/lenderTypes'
import type { SumerPositionInput } from './types'
import { EMPTY_BALANCE, computePostTradeMetrics } from '../utils'
import {
  getOrganicYieldDeposit,
  getRewardYieldDeposit,
  getIntrinsicYieldDeposit,
} from '../yield'
import {
  computeSumerWaterfall,
  buildSumerAccumulators,
  applyPositionDelta,
} from './waterfall'

/**
 * Compute post-trade balance metrics for a Sumer deposit operation.
 *
 * Unlike the standard deposit, Sumer must re-run the waterfall absorption
 * across ALL positions to determine the new effective collateral.
 */
export function computeSumerDepositDelta(
  amount: number,
  price: number,
  targetMarketUid: string,
  balanceData: BalanceData,
  positions: SumerPositionInput[],
  createNewSubAccount = false,
  apr?: AprData,
  yieldParams?: LenderYields,
): PostTradeMetrics {
  const base = createNewSubAccount ? EMPTY_BALANCE : balanceData
  const dollarAmount = amount * price

  const updatedPositions = applyPositionDelta(
    positions,
    targetMarketUid,
    dollarAmount,
    0,
    0,
  )

  const { groupRatesMap, gAccum, gAccumAll } =
    buildSumerAccumulators(updatedPositions)
  const groups = Object.values(groupRatesMap)
  const waterfallCollateral = computeSumerWaterfall(groups, gAccum)
  const waterfallCollateralAll = computeSumerWaterfall(groups, gAccumAll)

  const postTrade: BalanceData = {
    ...base,
    borrowDiscountedCollateral: waterfallCollateral,
    borrowDiscountedCollateralAllActive: waterfallCollateralAll,
    collateral: waterfallCollateral,
    collateralAllActive: waterfallCollateralAll,
    deposits: base.deposits + dollarAmount,
    debt: base.debt,
    adjustedDebt: base.debt,
    nav: base.deposits + dollarAmount - base.debt,
  }

  const result = computePostTradeMetrics(base, postTrade)

  if (apr && yieldParams) {
    const cashFlowDeposits = base.deposits * apr.depositApr
    const cashFlowDebt = base.debt * apr.borrowApr
    const rewardFlowDeposits = base.deposits * (apr.rewardDepositApr ?? 0)
    const rewardFlowDebt = base.debt * (apr.rewardBorrowApr ?? 0)
    const intrinsicFlowDeposits = base.deposits * apr.intrinsicDepositApr
    const intrinsicFlowDebt = base.debt * apr.intrinsicBorrowApr

    const newApr = { ...apr }
    ;[newApr.borrowApr, newApr.depositApr, newApr.apr] =
      getOrganicYieldDeposit(
        dollarAmount,
        cashFlowDeposits,
        cashFlowDebt,
        base.deposits,
        base.debt,
        yieldParams,
      )
    newApr.rewards = getRewardYieldDeposit(
      dollarAmount,
      rewardFlowDeposits,
      rewardFlowDebt,
      base.deposits,
      base.debt,
      yieldParams,
    )
    ;[
      newApr.intrinsicBorrowApr,
      newApr.intrinsicDepositApr,
      newApr.intrinsicApr,
    ] = getIntrinsicYieldDeposit(
      dollarAmount,
      intrinsicFlowDeposits,
      intrinsicFlowDebt,
      base.deposits,
      base.debt,
      yieldParams,
    )
    result.post.aprData = newApr
  }

  return result
}
